import React, { useEffect, useState } from 'react';
import { StatisticsService } from '../../services/statisticsService';
import { usePlayerStore } from '../../stores/playerStore';
import { 
  TrophyIcon, 
  HandRaisedIcon, 
  XMarkIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';

interface PairingStatsProps {
  className?: string;
}

interface PairingStatistic {
  players: string;
  wins: number;
  draws: number;
  losses: number;
  total: number;
  winRate: number;
}

const PairingStats: React.FC<PairingStatsProps> = ({ className = '' }) => {
  const { players } = usePlayerStore();
  const [pairingStats, setPairingStats] = useState<PairingStatistic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'total' | 'winRate' | 'wins'>('total');
  const [filterMinGames, setFilterMinGames] = useState(1);

  useEffect(() => {
    loadPairingStats();
  }, []);

  const loadPairingStats = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const stats = await StatisticsService.getPairingStatistics();
      
      // 计算胜率并格式化数据
      const formattedStats: PairingStatistic[] = stats.map(stat => ({
        ...stat,
        winRate: stat.total > 0 ? Math.round((stat.wins / stat.total) * 100) : 0
      }));

      setPairingStats(formattedStats);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载配对统计失败');
    } finally {
      setIsLoading(false);
    }
  };

  // 过滤和排序数据
  const filteredAndSortedStats = pairingStats
    .filter(stat => stat.total >= filterMinGames)
    .sort((a, b) => {
      switch (sortBy) {
        case 'total':
          return b.total - a.total;
        case 'winRate':
          return b.winRate - a.winRate;
        case 'wins':
          return b.wins - a.wins;
        default:
          return b.total - a.total;
      }
    });

  const getPerformanceColor = (winRate: number) => {
    if (winRate >= 70) return 'text-green-600 bg-green-50';
    if (winRate >= 50) return 'text-blue-600 bg-blue-50';
    if (winRate >= 30) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getPerformanceLabel = (winRate: number) => {
    if (winRate >= 70) return '优秀';
    if (winRate >= 50) return '良好';
    if (winRate >= 30) return '一般';
    return '需努力';
  };

  // 计算总体统计
  const totalStats = pairingStats.reduce(
    (acc, stat) => ({
      totalPairings: acc.totalPairings + 1,
      totalMatches: acc.totalMatches + stat.total,
      totalWins: acc.totalWins + stat.wins,
      totalDraws: acc.totalDraws + stat.draws,
      totalLosses: acc.totalLosses + stat.losses,
    }),
    { totalPairings: 0, totalMatches: 0, totalWins: 0, totalDraws: 0, totalLosses: 0 }
  );

  const averageWinRate = totalStats.totalMatches > 0 
    ? Math.round((totalStats.totalWins / totalStats.totalMatches) * 100)
    : 0;

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">配对统计</h2>
        <button
          onClick={loadPairingStats}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          刷新
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="text-red-600 text-sm">{error}</div>
        </div>
      )}

      {/* 总体统计 */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-700 mb-4">总体概览</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-800">
              {totalStats.totalPairings}
            </div>
            <div className="text-sm text-gray-600">配对组合</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {totalStats.totalMatches}
            </div>
            <div className="text-sm text-gray-600">总比赛数</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">
              {totalStats.totalWins}
            </div>
            <div className="text-sm text-gray-600">总胜利</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-yellow-600">
              {totalStats.totalDraws}
            </div>
            <div className="text-sm text-gray-600">总平局</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">
              {averageWinRate}%
            </div>
            <div className="text-sm text-gray-600">平均胜率</div>
          </div>
        </div>
      </div>

      {/* 筛选和排序控制 */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            排序方式
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="total">按比赛次数</option>
            <option value="winRate">按胜率</option>
            <option value="wins">按胜利次数</option>
          </select>
        </div>
        
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            最少比赛次数
          </label>
          <select
            value={filterMinGames}
            onChange={(e) => setFilterMinGames(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={1}>1场及以上</option>
            <option value={3}>3场及以上</option>
            <option value={5}>5场及以上</option>
            <option value={10}>10场及以上</option>
          </select>
        </div>
      </div>

      {/* 配对统计列表 */}
      {filteredAndSortedStats.length > 0 ? (
        <div className="space-y-4">
          {filteredAndSortedStats.map((stat, index) => (
            <PairingStatCard 
              key={`${stat.players}-${index}`}
              stat={stat}
              rank={index + 1}
              getPerformanceColor={getPerformanceColor}
              getPerformanceLabel={getPerformanceLabel}
            />
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-12">
          <UserGroupIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg mb-2">暂无配对数据</p>
          <p className="text-sm">
            {pairingStats.length === 0 
              ? '还没有比赛记录' 
              : '没有满足筛选条件的配对'
            }
          </p>
        </div>
      )}

      {/* 说明信息 */}
      <div className="text-xs text-gray-500 mt-6 pt-4 border-t">
        <div className="space-y-1">
          <div>• 配对统计显示每个搭档组合的比赛表现</div>
          <div>• 胜率 = 胜利次数 ÷ 总比赛次数 × 100%</div>
          <div>• 可以通过筛选条件查看不同层次的配对数据</div>
        </div>
      </div>
    </div>
  );
};

interface PairingStatCardProps {
  stat: PairingStatistic;
  rank: number;
  getPerformanceColor: (winRate: number) => string;
  getPerformanceLabel: (winRate: number) => string;
}

const PairingStatCard: React.FC<PairingStatCardProps> = ({
  stat,
  rank,
  getPerformanceColor,
  getPerformanceLabel
}) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          {/* 排名 */}
          <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
            {rank}
          </div>
          
          {/* 配对信息 */}
          <div>
            <div className="font-semibold text-gray-800 flex items-center space-x-2">
              <UserGroupIcon className="w-4 h-4 text-gray-500" />
              <span>{stat.players}</span>
            </div>
            <div className="text-sm text-gray-600">
              {stat.total} 场比赛
            </div>
          </div>
        </div>

        {/* 胜率和表现等级 */}
        <div className="text-right">
          <div className="text-lg font-bold text-gray-800 mb-1">
            {stat.winRate}%
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getPerformanceColor(stat.winRate)}`}>
            {getPerformanceLabel(stat.winRate)}
          </div>
        </div>
      </div>

      {/* 详细统计 */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <TrophyIcon className="w-4 h-4 text-green-600 mr-1" />
            <span className="text-lg font-bold text-green-600">{stat.wins}</span>
          </div>
          <div className="text-xs text-gray-600">胜利</div>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <HandRaisedIcon className="w-4 h-4 text-yellow-600 mr-1" />
            <span className="text-lg font-bold text-yellow-600">{stat.draws}</span>
          </div>
          <div className="text-xs text-gray-600">平局</div>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <XMarkIcon className="w-4 h-4 text-red-600 mr-1" />
            <span className="text-lg font-bold text-red-600">{stat.losses}</span>
          </div>
          <div className="text-xs text-gray-600">失败</div>
        </div>
      </div>

      {/* 胜率进度条 */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
          <span>胜率分布</span>
          <span>{stat.winRate}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${stat.winRate}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default PairingStats;